import * as React from 'react';
import { Dispatch, SetStateAction } from 'react';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import {
  GetProjectsCommandGq,
  GetProjectsResponseGq,
} from '../../../../graphql/projects/queries/useGetProjectsGq';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import VoxelSwitch from '../../../voxel/Forms/VoxelSwitch';
import VoxelPanel from '../../../voxel/Surfaces/VoxelPanel';
import VoxelTable from '../../../voxel/Tables/VoxelTable';
import VoxelTableHeadCell from '../../../voxel/Tables/VoxelTableHeadCell';
import VoxelTablePagination from '../../../voxel/Tables/VoxelTablePagination';
import VoxelTableRow from '../../../voxel/Tables/VoxelTableRow';
import VoxelTableRowEmpty from '../../../voxel/Tables/VoxelTableRowEmpty';
import VoxelAdvancedTextSearch from '../../../voxel/Search/VoxelAdvancedTextSearch';
import ProjectHeaderCell from './ProjectHeaderCell';
import ProjectTableRow from './ProjectTableRow';
import VoxelButton from '../../../voxel/Buttons/VoxelButton';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface ProjectTableProps {
  projectsData?: GetProjectsResponseGq;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  filterState: GetProjectsCommandGq;
  setFilterState: Dispatch<SetStateAction<GetProjectsCommandGq>>;
  setShowCopyProgressId?: (id: number) => void;
  copyProject?: (id: ProjectGq) => void;
  setShowTranslateProgressId?: (id: number) => void;
  translateProject?: (id: ProjectGq) => void;
  showOptions?: boolean;
  rowComponent?: 'a' | 'div';
  onClick?: (row: ProjectGq) => void;
  highlightedProjectIds?: number[];
  getHighlightedProject?: (project: ProjectGq) => void;
  user?: CurrentUserGq;
  allowTeamIdFilter?: number;
  pristineFilter?: boolean;
  resetFilter?: () => void;
}

// We memoize the component for improved performance during search bar input
const ProjectTable: React.FC<ProjectTableProps> = React.memo<ProjectTableProps>(
  ({
    projectsData,
    sortBy,
    sortDirection,
    filterState,
    setFilterState,
    setShowCopyProgressId,
    copyProject,
    setShowTranslateProgressId,
    translateProject,
    showOptions,
    rowComponent = 'div',
    onClick,
    highlightedProjectIds,
    getHighlightedProject,
    user,
    allowTeamIdFilter,
    pristineFilter,
    resetFilter,
  }) => {
    return (
      <>
        <VoxelPanel className="overflow-visible">
          <div className="p-2 flex flex-row gap-2 items-center">
            <VoxelAdvancedTextSearch
              size="sm"
              value={filterState.searchTerm}
              onChange={(event) => {
                setFilterState((prev) => ({ ...prev, page: 0, searchTerm: event.target.value }));
              }}
              placeholder={'Name, website, team, user'}
              widthClass="w-64"
              deleteValue={() => {
                setFilterState((prev) => ({ ...prev, page: 0, searchTerm: '' }));
              }}
            />
            {!pristineFilter && (
              <VoxelButton
                endIcon={<FontAwesomeIcon icon={faTimes} />}
                size="md"
                title="Active filters"
                color="gray"
                onClick={resetFilter}
              />
            )}
            <div className="flex-grow" />
            <VoxelSwitch
              title="Mine"
              value={filterState.userOnly}
              color="primary"
              onChange={(value: boolean) =>
                setFilterState((prev) => ({ ...prev, userOnly: value }))
              }
            />
            <VoxelSwitch
              title={'With rank tracking'}
              color="primary"
              value={!!filterState.progressEnabled}
              onChange={(value: boolean) =>
                setFilterState((prev) => ({ ...prev, progressEnabled: value ? true : false }))
              }
            />
            <VoxelSwitch
              title={'Archived'}
              color="primary"
              value={filterState.showArchived}
              onChange={(value: boolean) =>
                setFilterState((prev) => ({ ...prev, showArchived: value }))
              }
            />
          </div>
          <VoxelTable className="select-none">
            <thead>
              <VoxelTableRow>
                <VoxelTableHeadCell>Project</VoxelTableHeadCell>
                <VoxelTableHeadCell>Language</VoxelTableHeadCell>
                <VoxelTableHeadCell>Location</VoxelTableHeadCell>
                <ProjectHeaderCell
                  sortBy={'createTime'}
                  setFilterState={setFilterState}
                  currentDirection={sortDirection}
                  currentSortBy={sortBy}
                >
                  Created
                </ProjectHeaderCell>
                <VoxelTableHeadCell align="center">User</VoxelTableHeadCell>
                <VoxelTableHeadCell align="left">Stats</VoxelTableHeadCell>
                {showOptions && <VoxelTableHeadCell />}
              </VoxelTableRow>
            </thead>
            <tbody>
              {!projectsData?.projects?.length ? (
                <VoxelTableRowEmpty colSpan={8}>No projects found</VoxelTableRowEmpty>
              ) : (
                projectsData?.projects?.map((project) => (
                  <ProjectTableRow
                    project={project}
                    key={project.id}
                    setShowCopyProgressId={setShowCopyProgressId}
                    copyProject={copyProject}
                    setShowTranslateProgressId={setShowTranslateProgressId}
                    translateProject={translateProject}
                    rowComponent={rowComponent}
                    onClick={onClick}
                    showOptions={showOptions}
                    user={user}
                    highlighedProjectIds={highlightedProjectIds}
                    getHighlightedProject={getHighlightedProject}
                  />
                ))
              )}
              <VoxelTablePagination
                currentPage={projectsData?.page}
                rowsPerPage={projectsData?.amountPerPage}
                totalCount={projectsData?.totalCount}
                onPageChange={(_event, page) => setFilterState((prev) => ({ ...prev, page }))}
                colSpan={8}
              />
            </tbody>
          </VoxelTable>
        </VoxelPanel>
      </>
    );
  }
);

export default ProjectTable;
